import { useMemo } from 'react';
import { Box, Switch, Text, Tr, VStack } from '@chakra-ui/react';
import {
    SettingsCard,
    SettingsInlineNumberEdit,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from '../../../../components/settings/primitives';
import type { LocationDefaultTaxRate } from '../../../../hooks/useGetSettings';
import { isCanadianLocation } from '../../../../utils/isCanadianLocation';
import {
    percentToTaxRateDecimal,
    taxRateDecimalToPercent,
    upsertLocationDefaultTaxRate,
    useInvoicingDraft,
} from './shared';
import type { InvoicingSectionProps } from './types';

/**
 * A rate cell at rest. Blank reads as a dash rather than 0%, since no rate
 * configured and a zero rate mean different things to the tax toggles.
 */
const RateDisplay = ({ value }: { value: number | null }) => (
    <Text fontSize="sm" color={value == null ? 'gray.400' : 'gray.700'}>
        {value == null ? '—' : `${value}%`}
    </Text>
);

const TaxesSection = ({ data, locations }: InvoicingSectionProps) => {
    const { invoicing, updateInvoicing, isSaving } = useInvoicingDraft(data);

    const locationDefaultTaxRates = useMemo(
        () => invoicing.locationDefaultTaxRates ?? [],
        [invoicing.locationDefaultTaxRates],
    );
    const taxRateLocations = useMemo(
        () =>
            [...locations].sort((a, b) =>
                a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
            ),
        [locations],
    );

    const locationPaging = useSubTablePagination(taxRateLocations);

    const getLocationTaxRateEntry = (locationId: string): LocationDefaultTaxRate | undefined =>
        locationDefaultTaxRates.find((entry) => entry.locationId === locationId);

    const handleLocationTaxToggle = (
        locationId: string,
        field: 'applyToReceived' | 'applyToCredited' | 'applyToSublet',
        value: boolean,
    ) => {
        updateInvoicing({
            locationDefaultTaxRates: upsertLocationDefaultTaxRate(
                locationDefaultTaxRates,
                locationId,
                { [field]: value },
            ),
        });
    };

    const handleRateChange = (
        locationId: string,
        field: 'taxRate' | 'pstTaxRate',
        percent: number | null,
    ) => {
        updateInvoicing({
            locationDefaultTaxRates: upsertLocationDefaultTaxRate(
                locationDefaultTaxRates,
                locationId,
                { [field]: percent == null ? undefined : percentToTaxRateDecimal(percent) },
            ),
        });
    };

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard
                title="Default tax rate by location"
                description="If invoices in your region frequently include tax, add the tax rate for each store here. Tax is submitted as an invoice total, not as a line item."
                dividers={false}
            >
                {/* py matches SettingsRow so the table lines up with the rows
                    of the cards around it. */}
                <Box py={4}>
                    <SettingsSubTable
                        isInset
                        currentPage={locationPaging.currentPage}
                        totalPages={locationPaging.totalPages}
                        onPageChange={locationPaging.setCurrentPage}
                        head={
                            <>
                                <SettingsTh>Location</SettingsTh>
                                <SettingsTh>Tax rate</SettingsTh>
                                <SettingsTh>PST rate</SettingsTh>
                                <SettingsTh textAlign="center">Received</SettingsTh>
                                <SettingsTh textAlign="center">Credited</SettingsTh>
                                <SettingsTh textAlign="center">Sublet</SettingsTh>
                            </>
                        }
                    >
                        {taxRateLocations.length === 0 ? (
                            <Tr>
                                <SettingsTd colSpan={6} py={6} color="gray.500">
                                    No shop locations found.
                                </SettingsTd>
                            </Tr>
                        ) : (
                            locationPaging.pageItems.map((location) => {
                                const entry = getLocationTaxRateEntry(location._id);
                                const hasRate = entry?.taxRate != null;
                                return (
                                    <Tr key={location._id}>
                                        <SettingsTd>{location.title}</SettingsTd>
                                        <SettingsTd>
                                            <SettingsInlineNumberEdit
                                                display={
                                                    <RateDisplay
                                                        value={
                                                            entry?.taxRate != null
                                                                ? taxRateDecimalToPercent(
                                                                      entry.taxRate,
                                                                  )
                                                                : null
                                                        }
                                                    />
                                                }
                                                value={
                                                    entry?.taxRate != null
                                                        ? taxRateDecimalToPercent(entry.taxRate)
                                                        : null
                                                }
                                                onSave={(next) =>
                                                    handleRateChange(location._id, 'taxRate', next)
                                                }
                                                min={0}
                                                max={100}
                                                unit="%"
                                                width="120px"
                                                ariaLabel={`Tax rate for ${location.title}`}
                                                isDisabled={isSaving}
                                            />
                                        </SettingsTd>
                                        <SettingsTd>
                                            {isCanadianLocation(location.state) ? (
                                                <SettingsInlineNumberEdit
                                                    display={
                                                        <RateDisplay
                                                            value={
                                                                entry?.pstTaxRate != null
                                                                    ? taxRateDecimalToPercent(
                                                                          entry.pstTaxRate,
                                                                      )
                                                                    : null
                                                            }
                                                        />
                                                    }
                                                    value={
                                                        entry?.pstTaxRate != null
                                                            ? taxRateDecimalToPercent(
                                                                  entry.pstTaxRate,
                                                              )
                                                            : null
                                                    }
                                                    onSave={(next) =>
                                                        handleRateChange(
                                                            location._id,
                                                            'pstTaxRate',
                                                            next,
                                                        )
                                                    }
                                                    min={0}
                                                    max={100}
                                                    unit="%"
                                                    width="120px"
                                                    ariaLabel={`PST rate for ${location.title}`}
                                                    isDisabled={isSaving}
                                                />
                                            ) : (
                                                <Text fontSize="sm" color="gray.400">
                                                    N/A
                                                </Text>
                                            )}
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            <Switch
                                                size="md"
                                                colorScheme="partbay-blue"
                                                isChecked={entry?.applyToReceived ?? false}
                                                isDisabled={!hasRate || isSaving}
                                                onChange={(e) =>
                                                    handleLocationTaxToggle(
                                                        location._id,
                                                        'applyToReceived',
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            <Switch
                                                size="md"
                                                colorScheme="partbay-blue"
                                                isChecked={entry?.applyToCredited ?? false}
                                                isDisabled={!hasRate || isSaving}
                                                onChange={(e) =>
                                                    handleLocationTaxToggle(
                                                        location._id,
                                                        'applyToCredited',
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            <Switch
                                                size="md"
                                                colorScheme="partbay-blue"
                                                isChecked={entry?.applyToSublet ?? false}
                                                isDisabled={!hasRate || isSaving}
                                                onChange={(e) =>
                                                    handleLocationTaxToggle(
                                                        location._id,
                                                        'applyToSublet',
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                        </SettingsTd>
                                    </Tr>
                                );
                            })
                        )}
                    </SettingsSubTable>
                </Box>
            </SettingsCard>
        </VStack>
    );
};

export default TaxesSection;
